import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { ToastrService } from 'ngx-toastr';
import { Message } from 'src/app/core/class/message';
import { defer, of, Subject, timer } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, map, switchMap, take, takeUntil, timeout } from 'rxjs/operators';
import { AppendixFilterRow, LinkDocumentAppendixService } from './link-document-appendix.service';

export interface LinkDocumentAppendixItem {
  id: string | number;
  name: string;
  documentCode: string;
  /** ISO calendar date, displayed through the common Angular date pipe. */
  signedDate?: string;
  selected?: boolean;
  disabled?: boolean;
  source?: AppendixFilterRow;
}

export interface LinkDocumentAppendixWorker {
  certificateNo: string;
  name: string;
  codeAndName: string;
}

export interface LinkDocumentAppendixDialogData {
  worker: LinkDocumentAppendixWorker;
  contractOriginalId: string | number;
  /** Actual employee enterprise ID from the employee record; not the CCCD. */
  employeeEnterpriseId: string;
}

export interface LinkDocumentAppendixDialogResult {
  action: 'create-new' | 'link';
  appendices?: LinkDocumentAppendixItem[];
}

@Component({
  selector: 'app-link-document-appendix-dialog',
  templateUrl: './link-document-appendix-dialog.component.html',
  styleUrls: ['./link-document-appendix-dialog.component.scss']
})
export class LinkDocumentAppendixDialogComponent implements OnInit, OnDestroy {
  searchText = '';
  appendices: LinkDocumentAppendixItem[] = [];
  submitting = false;
  loading = false;
  loadError = false;
  loadErrorMessage = '';
  linkErrorMessage = '';
  private readonly searches = new Subject<{ keyword: string; delay: number }>();
  private readonly selection = new Map<string | number, LinkDocumentAppendixItem>();
  private readonly destroyed = new Subject<void>();
  private disposed = false;

  constructor(
    public dialogRef: MatDialogRef<LinkDocumentAppendixDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: LinkDocumentAppendixDialogData,
    private toastr: ToastrService,
    private appendixService: LinkDocumentAppendixService,
    public message: Message,
    private cdr: ChangeDetectorRef
  ) {
    this.dialogRef.disableClose = false;
  }

  ngOnInit(): void {
    this.searches.pipe(
      map(s => ({
        keyword: (s.keyword || '').trim(),
        delay: s.delay
      })),
      distinctUntilChanged((prev, curr) => curr.delay > 0 && prev.keyword === curr.keyword),
      switchMap(search => {
        const delay$ = search.delay > 0 ? timer(search.delay) : of(0);
        return delay$.pipe(
          switchMap(() => {
            const employeeEnterpriseId = this.getEmployeeEnterpriseId();
            if (!employeeEnterpriseId) {
              this.loading = false;
              this.loadError = true;
              this.loadErrorMessage = this.message.getMessage('messages.error_message');
              this.cdr.markForCheck();
              return of([] as LinkDocumentAppendixItem[]);
            }

            this.loading = true;
            this.loadError = false;
            this.loadErrorMessage = '';
            this.cdr.markForCheck();

            return this.appendixService.filter({
              employeeEnterpriseId: employeeEnterpriseId,
              nameOrCode: search.keyword
            }).pipe(
              timeout(15000),
              map(response => {
                if (response && response.success === false) {
                  const msg = (response.message || '').toLowerCase();
                  if (msg.includes('không tìm thấy') || msg.includes('không có') || msg.includes('not found')) {
                    return [];
                  }
                  throw new Error(response.message || this.message.getMessage('messages.error_message'));
                }
                const rows = this.extractRows(response);
                const seen = new Set<string>();
                return rows.filter(row => {
                  if (!row || !row.id || seen.has(row.id)) { return false; }
                  seen.add(row.id);
                  return true;
                }).map(row => this.mapAppendix(row));
              }),
              catchError(error => {
                this.loadError = true;
                this.loadErrorMessage = typeof error === 'string' ? error :
                  (error && error.message) || this.message.getMessage('messages.error_message');
                return of([] as LinkDocumentAppendixItem[]);
              }),
              finalize(() => {
                this.loading = false;
                this.cdr.markForCheck();
              })
            );
          })
        );
      }),
      takeUntil(this.destroyed)
    ).subscribe(
      appendices => {
        this.appendices = appendices;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error => {
        this.loading = false;
        this.loadError = true;
        this.loadErrorMessage = (error && error.message) || this.message.getMessage('messages.error_message');
        this.cdr.markForCheck();
      }
    );
    this.retrySearch();
  }

  private getEmployeeEnterpriseId(): string {
    if (!this.data) {
      return '';
    }
    const anyData = this.data as any;
    const rawId = anyData.raw && anyData.raw.idPersonal ? anyData.raw.idPersonal : '';
    const workerEmpId = anyData.worker && anyData.worker.employeeEnterpriseId ? anyData.worker.employeeEnterpriseId : '';
    const id = this.data.employeeEnterpriseId ||
      anyData.idPersonal ||
      anyData.personalId ||
      rawId ||
      workerEmpId ||
      '';
    return String(id).trim();
  }

  private extractRows(response: any): AppendixFilterRow[] {
    if (!response) {
      return [];
    }
    if (Array.isArray(response)) {
      return response;
    }
    if (Array.isArray(response.dataRow)) {
      return response.dataRow;
    }
    if (response.data) {
      if (Array.isArray(response.data)) {
        return response.data;
      }
      if (Array.isArray(response.data.dataRow)) {
        return response.data.dataRow;
      }
      if (Array.isArray(response.data.content)) {
        return response.data.content;
      }
    }
    return [];
  }

  onSearchChange(value: string): void {
    this.searchText = (value || '').slice(0, 255);
    this.searches.next({ keyword: this.searchText, delay: 300 });
  }

  onBlurSearch(): void {
    const trimmed = (this.searchText || '').trim();
    if (this.searchText !== trimmed) {
      this.searchText = trimmed;
      this.searches.next({ keyword: this.searchText, delay: 0 });
    }
  }

  retrySearch(): void {
    this.searches.next({ keyword: this.searchText, delay: 0 });
  }

  get selectedAppendices(): LinkDocumentAppendixItem[] {
    return Array.from(this.selection.values());
  }

  trackAppendix(index: number, appendix: LinkDocumentAppendixItem): string | number {
    return appendix.id;
  }

  toggleAppendix(appendix: LinkDocumentAppendixItem, selected: boolean): void {
    if (this.submitting || appendix.disabled) {
      return;
    }

    appendix.selected = selected;
    if (selected) {
      this.selection.set(appendix.id, appendix);
    } else {
      this.selection.delete(appendix.id);
    }
  }

  onNoClick(): void {
    if (this.submitting) { return; }
    this.dialogRef.close();
  }

  confirm(): void {
    if (this.submitting) { return; }
    const selected = this.selectedAppendices;
    if (selected.length === 0) {
      this.toastr.error(this.message.getMessage('record_management_moha.message.select_appendix_to_link'));
      return;
    }
    if (this.data.contractOriginalId == null || String(this.data.contractOriginalId).trim() === '') {
      this.toastr.error(this.message.getMessage('record_management_moha.message.missing_original_contract_info'));
      return;
    }

    this.linkErrorMessage = '';
    this.submitting = true;
    this.dialogRef.disableClose = true;
    let receivedResponse = false;
    defer(() => this.appendixService.addContractAppendix({
      contractOriginalId: String(this.data.contractOriginalId),
      contractAppendixId: selected.map(appendix => String(appendix.id))
    })).pipe(
      take(1),
      takeUntil(this.destroyed),
      finalize(() => {
        this.submitting = false;
        this.dialogRef.disableClose = false;
      })
    ).subscribe(response => {
      receivedResponse = true;
      if (response && response.success === true) {
        this.toastr.success(this.message.getMessage('record_management_moha.message.create_dossier_success'));
        this.dialogRef.close(true);
      } else {
        // The backend supplies the full BNV conflict/business message.
        this.linkErrorMessage = response && response.message || this.message.getMessage('messages.error_message');
        this.toastr.error(this.linkErrorMessage);
      }
    }, error => {
      // HTTP errors are notified by the base interceptor; retain an inline retry state.
      this.linkErrorMessage = typeof error === 'string' ? error :
        (error && error.message) || this.message.getMessage('messages.error_message');
    }, () => {
      if (!receivedResponse && !this.disposed) {
        this.linkErrorMessage = this.message.getMessage('messages.error_message');
        this.toastr.error(this.linkErrorMessage);
      }
    });
  }

  ngOnDestroy(): void {
    this.disposed = true;
    this.destroyed.next();
    this.destroyed.complete();
  }

  private mapAppendix(row: AppendixFilterRow): LinkDocumentAppendixItem {
    const item: LinkDocumentAppendixItem = {
      id: row.id,
      name: row.contractName,
      documentCode: row.contractCode,
      signedDate: this.normalizeSignedDate(row.signedDate),
      selected: this.selection.has(row.id),
      source: row
    };
    if (item.selected) { this.selection.set(item.id, item); }
    return item;
  }

  private normalizeSignedDate(value: string): string {
    if (!value) { return ''; }
    // Parse the documented English date explicitly, without browser-dependent parsing.
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const legacy = /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2}),\s+(\d{4})\b/.exec(value);
    const iso = /^(\d{4})-(\d{2})-(\d{2})(?:T|\s|$)/.exec(value);
    const normalized = legacy ? legacy[3] + '-' +
      ('0' + (months.indexOf(legacy[1]) + 1)).slice(-2) + '-' + ('0' + legacy[2]).slice(-2) :
      (iso ? iso[1] + '-' + iso[2] + '-' + iso[3] : '');
    if (!normalized) { return ''; }
    const date = new Date(normalized + 'T00:00:00Z');
    return !isNaN(date.getTime()) && date.toISOString().slice(0, 10) === normalized ? normalized : '';
  }
}
